import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import { createSearchIndex, processClientData } from "../utils/clientSearch";
import { getPaginatedData, getTotalPages } from "../utils/clientPagination";
import { useApp } from "./AppContext";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useSalon } from "./SalonContext";
import { removeNullValues } from "../utils/apiUtils";
import { collapsePotentialDuplicates } from "../utils/duplicateGrouping";

const ClientContext = createContext();

export function ClientProvider({ children }) {
  const { fetchWithAuth } = useApp();
  const { businessInfo } = useSalon();
  const [clients, setClients] = useState([]);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    isOptedInReminder: false,
    isOptedInMarketing: false,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sorting, setSorting] = useState({
    field: "first_name",
    direction: "asc",
  });

  // Create clientsById mapping
  const clientsById = useMemo(() => {
    return clients.reduce((acc, client) => {
      acc[client.id] = client;
      return acc;
    }, {});
  }, [clients]);

  // Create search index when clients change
  const searchIndex = useMemo(() => {
    return createSearchIndex(clients);
  }, [clients]);

  // Process clients based on search, filters, and sorting
  const processedClients = useMemo(() => {
    return processClientData({
      clients,
      searchIndex,
      searchQuery,
      filters,
      sorting,
    });
  }, [clients, searchIndex, searchQuery, filters, sorting]);

  // Get paginated data
  const paginatedClients = useMemo(() => {
    return getPaginatedData(processedClients, page, pageSize);
  }, [processedClients, page, pageSize]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return getTotalPages(processedClients.length, pageSize);
  }, [processedClients.length, pageSize]);

  const fetchClients = async () => {
    if (!businessInfo?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_CLIENTS, "", {
          salonId: businessInfo.id,
        }),
      );

      if (!response?.clients) {
        throw new Error("Invalid response format from server");
      }

      setClients(response.clients);
      // Collapse per-criterion groups into one entry per unique client set
      // (works whether get_clients sends the legacy or the collapsed shape).
      setPotentialDuplicates(
        collapsePotentialDuplicates(response.potential_duplicates),
      );
    } catch (err) {
      setError(err.message || "Failed to fetch clients");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const createClient = async (clientData) => {
    if (!businessInfo?.id) {
      throw new Error("No salon ID available");
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENTS),
        {
          method: "POST",
          body: JSON.stringify(
            removeNullValues({
              ...clientData,
              salon_uuid: businessInfo.id,
            }),
          ),
        },
      );

      // Add the new client to the list
      setClients((prevClients) => [...prevClients, response.client]);
      return response;
    } catch (err) {
      setError(err.message || "Failed to create client");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateClient = async (clientId, clientData) => {
    if (!businessInfo?.id) {
      throw new Error("No salon ID available");
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENTS_BY_ID, "", { clientId }),
        {
          method: "PUT",
          body: JSON.stringify(clientData),
        },
      );

      // Update the client in the list
      setClients((prevClients) =>
        prevClients.map((client) => {
          if (client.id === clientId) {
            return {
              ...client,
              ...response.client,
            };
          }
          return client;
        }),
      );
      return response;
    } catch (err) {
      setError(err.message || "Failed to update client");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteClient = async (clientId) => {
    if (!businessInfo?.id) {
      throw new Error("No salon ID available");
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENTS_BY_ID, "", { clientId }),
        {
          method: "PUT",
          body: JSON.stringify({ status: "Inactive" }),
        },
      );

      // Remove the client from the list
      setClients((prevClients) =>
        prevClients.filter((client) => client.id !== clientId),
      );
      return response;
    } catch (err) {
      setError(err.message || "Failed to delete client");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Merge 2-3 client records into a single surviving (winning) profile.
   * Re-points all historical data (appointments, transactions, notes,
   * formulations, payment methods, forms, documents, etc.) onto the winner
   * and soft-deletes the losing records. This action cannot be undone.
   *
   * @param {string} winningClientId - UUID of the client to keep
   * @param {string[]} losingClientIds - UUIDs of the clients to merge in & remove
   * @param {object} fieldValues - chosen "winning" scalar values for the merged profile
   * @returns {Promise<{client: object, merge_summary: object}>}
   */
  const mergeClients = async (
    winningClientId,
    losingClientIds,
    fieldValues = {},
  ) => {
    if (!businessInfo?.id) {
      throw new Error("No salon ID available");
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.MERGE_CLIENTS, "", {
          salonId: businessInfo.id,
        }),
        {
          method: "POST",
          body: JSON.stringify({
            winning_client_id: winningClientId,
            losing_client_ids: losingClientIds,
            field_values: removeNullValues(fieldValues),
          }),
        },
      );

      // Re-fetch so the merged-away records disappear and duplicate flags refresh.
      await fetchClients();
      return response;
    } catch (err) {
      setError(err.message || "Failed to merge clients");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Remove a client from local state by uuid. Used by the Pusher client-sync
   * listener when another tab / device deletes or merges away a client so
   * the row disappears from the list without a full refetch.
   */
  const evictClientById = (clientId) => {
    if (!clientId) return;
    setClients((prevClients) =>
      prevClients.filter((client) => client.id !== clientId),
    );
  };

  /**
   * Re-fetch a single client from the API and merge into local state (e.g. after card on file).
   * Does not toggle global clients loading.
   */
  const refreshClientById = async (clientId) => {
    if (!clientId) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENTS_BY_ID, "", { clientId }),
      );

      if (response?.client) {
        setClients((prevClients) => {
          const idx = prevClients.findIndex((c) => c.id === clientId);
          if (idx >= 0) {
            const next = [...prevClients];
            next[idx] = { ...next[idx], ...response.client };
            return next;
          }
          return [...prevClients, response.client];
        });
      }
    } catch (err) {
      console.error("Failed to refresh client:", err);
      throw err;
    }
  };

  /**
   * Create or update a client note
   * @param {string} clientId - The client ID
   * @param {object} noteData - { text: string, id?: string } - include id for updates
   * @returns {Promise<{note: object}>}
   */
  const createOrUpdateClientNote = async (clientId, noteData) => {
    try {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_NOTE, "", { clientId }),
        {
          method: "POST",
          body: JSON.stringify(noteData),
        },
      );

      // Update the client's notes in local state
      setClients((prevClients) =>
        prevClients.map((client) => {
          if (client.id === clientId) {
            const existingNotes = client.notes || [];
            const noteIndex = existingNotes.findIndex(
              (n) => n.id === response.note.id,
            );

            let updatedNotes;
            if (noteIndex >= 0) {
              // Update existing note
              updatedNotes = [...existingNotes];
              updatedNotes[noteIndex] = response.note;
            } else {
              // Add new note at the beginning (most recent first)
              updatedNotes = [response.note, ...existingNotes];
            }

            return { ...client, notes: updatedNotes };
          }
          return client;
        }),
      );

      return response;
    } catch (err) {
      throw err;
    }
  };

  /**
   * Delete a client note
   * @param {string} clientId - The client ID
   * @param {string} noteId - The note ID to delete
   * @returns {Promise<{message: string, id: string}>}
   */
  const deleteClientNote = async (clientId, noteId) => {
    try {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_NOTE_BY_ID, "", {
          clientId,
          noteId,
        }),
        {
          method: "DELETE",
        },
      );

      // Remove the note from local state
      setClients((prevClients) =>
        prevClients.map((client) => {
          if (client.id === clientId) {
            return {
              ...client,
              notes: (client.notes || []).filter((n) => n.id !== noteId),
            };
          }
          return client;
        }),
      );

      return response;
    } catch (err) {
      throw err;
    }
  };

  /**
   * Create or update a client formulation
   * @param {string} clientId - The client ID
   * @param {object} formulationData - { text: string, id?: string } - include id for updates
   * @returns {Promise<{formulation: object}>}
   */
  const createOrUpdateClientFormulation = async (clientId, formulationData) => {
    try {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORMULATION, "", { clientId }),
        {
          method: "POST",
          body: JSON.stringify(formulationData),
        },
      );

      // Update the client's formulations in local state
      setClients((prevClients) =>
        prevClients.map((client) => {
          if (client.id === clientId) {
            const existingFormulations = client.formulations || [];
            const formulationIndex = existingFormulations.findIndex(
              (f) => f.id === response.formulation.id,
            );

            let updatedFormulations;
            if (formulationIndex >= 0) {
              // Update existing formulation
              updatedFormulations = [...existingFormulations];
              updatedFormulations[formulationIndex] = response.formulation;
            } else {
              // Add new formulation at the beginning (most recent first)
              updatedFormulations = [
                response.formulation,
                ...existingFormulations,
              ];
            }

            return { ...client, formulations: updatedFormulations };
          }
          return client;
        }),
      );

      return response;
    } catch (err) {
      throw err;
    }
  };

  /**
   * Delete a client formulation
   * @param {string} clientId - The client ID
   * @param {string} formulationId - The formulation ID to delete
   * @returns {Promise<{message: string, id: string}>}
   */
  const deleteClientFormulation = async (clientId, formulationId) => {
    try {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORMULATION_BY_ID, "", {
          clientId,
          formulationId,
        }),
        {
          method: "DELETE",
        },
      );

      // Remove the formulation from local state
      setClients((prevClients) =>
        prevClients.map((client) => {
          if (client.id === clientId) {
            return {
              ...client,
              formulations: (client.formulations || []).filter(
                (f) => f.id !== formulationId,
              ),
            };
          }
          return client;
        }),
      );

      return response;
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    if (businessInfo?.id) {
      fetchClients();
    } else {
      setClients([]);
    }
  }, [businessInfo?.id]);

  // [LOOP] churn probe
  useValueChurnProbe("ClientContext", { clients, loading, error });

  const value = {
    allClients: clients,
    clients: paginatedClients,
    clientsById,
    potentialDuplicates,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalClients: processedClients.length,
    sorting,
    setSorting,
    refreshClients: fetchClients,
    refreshClientById,
    evictClientById,
    createClient,
    updateClient,
    deleteClient,
    mergeClients,
    // Notes and Formulations
    createOrUpdateClientNote,
    deleteClientNote,
    createOrUpdateClientFormulation,
    deleteClientFormulation,
  };

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
}

export function useClient() {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error("useClient must be used within a ClientProvider");
  }
  return context;
}
